// Seção que abre e fecha tocando no título (texto 07 A2 e D1): o componente ÚNICO de seção recolhível do app.
// Seta ▾ quando aberta, ▸ quando recolhida, SEMPRE à esquerda do título (0.72, texto 10 C5: o mesmo lado em todo o app;
//   seta="direita" ficou só por compatibilidade, ninguém usa).
// Recolhida mostra só o título e a seta. Tocar no título ou na seta abre e fecha.
// chave: o app lembra o que a pessoa deixou aberto ou fechado NESTE aparelho (lib/recolher.ts; o que já estava em
//   ajustes.secoes vale como ponto de partida). Sem chave, o estado vale só enquanto a tela está aberta.
// estado + onAlternar: quem chama guarda o estado em outro lugar (ex.: Bem-estar, que já guarda as seções na nuvem).
// visual: 'linha' (título em negrito, sem fundo; padrão), 'cartao' (faixa com fundo de cartão, como nas abas)
//   ou 'titulo' (título de bloco em maiúsculas, como em Ajustes).
import { useState, type ReactNode } from 'react';
import { Pressable, Text, View, type TextStyle, type ViewStyle } from 'react-native';
import { C } from './theme';
import { useCaixas } from '../lib/caixas';
import { useDoc } from '../store/store';
import { setAberto, useAberto } from '../lib/recolher';
import { t } from '../lib/i18n';

export type RecolhivelProps = {
  titulo: string; children: ReactNode;
  /** Aberta na primeira vez (sem nada guardado). */
  inicial?: boolean;
  chave?: string;
  estado?: boolean; onAlternar?: (aberto: boolean) => void;
  /** Mostra aberta sem mudar o guardado (ex.: durante uma busca). */
  forcarAberto?: boolean;
  visual?: 'linha' | 'cartao' | 'titulo';
  seta?: 'direita' | 'esquerda';
  /** Texto curto antes da seta (ex.: "3 marcado(s)"). */
  resumo?: string;
  /** Algo a mais na linha do título, antes da seta (ex.: um botão "+"). */
  direita?: ReactNode;
  cor?: string; gap?: number;
  estiloTitulo?: TextStyle; estiloBarra?: ViewStyle; estiloSeta?: TextStyle;
  /** Linhas do título (0 = sem limite). */
  linhas?: number;
  hitSlop?: number;
  /** Mostra só o conteúdo, sem título nem seta. */
  desligado?: boolean;
  /** Sempre aberta: mostra o título, sem seta, e não recolhe (0.69.1: bloco Assinatura dos Ajustes). */
  fixo?: boolean;
};

export function Recolhivel({ titulo, children, inicial = false, chave, estado, onAlternar, forcarAberto, visual = 'linha', seta = 'esquerda',
  resumo, direita, cor, gap, estiloTitulo, estiloBarra, estiloSeta, linhas, hitSlop = 6, desligado, fixo }: RecolhivelProps) {
  const m = useCaixas();
  const secoes = useDoc('ajustes').secoes;
  const [local, setLocal] = useState(inicial);
  const guardado = useAberto(chave, chave ? (secoes?.[chave] ?? inicial) : inicial);
  const atual = estado ?? (chave ? guardado : local);
  const aberto = !!forcarAberto || !!fixo || atual;
  const espaco = gap ?? (visual === 'linha' ? 6 : m.gap(10));
  if (desligado) return <View style={{ gap: espaco }}>{children}</View>;
  const alternar = () => {
    const novo = !atual;
    if (onAlternar) return onAlternar(novo);
    if (chave) return setAberto(chave, novo);
    setLocal(novo);
  };

  const barra: ViewStyle = visual === 'titulo'
    ? { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14, paddingVertical: 2 }
    : visual === 'cartao'
      ? { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: m.pad(10), paddingHorizontal: m.padH(14), borderRadius: m.raio(14),
          backgroundColor: C.card, borderWidth: 1, borderColor: C.line }
      : { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8 };
  const opac = visual === 'cartao' ? 0.7 : 0.6;
  const estTitulo: TextStyle = visual === 'titulo'
    ? { flex: 1, color: cor ?? C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }
    : visual === 'cartao'
      ? { flex: 1, color: cor ?? C.text, fontSize: 13, fontWeight: '800', letterSpacing: 0.4 }
      : { flex: 1, color: cor ?? C.text, fontWeight: '700' };
  const estSeta: TextStyle = visual === 'titulo' ? { color: cor ?? C.sub, fontSize: 13 } : visual === 'cartao' ? { color: C.sub, fontSize: 14 } : { color: C.mute, fontSize: 16 };
  const nLinhas = linhas ?? (visual === 'linha' ? 0 : 1);
  const setinha = <Text style={[estSeta, estiloSeta]}>{aberto ? '▾' : '▸'}</Text>;

  return (
    <View style={{ gap: espaco }}>
      <Pressable onPress={alternar} disabled={!!fixo} hitSlop={hitSlop} accessibilityRole={fixo ? "header" : "button"} accessibilityState={fixo ? undefined : { expanded: aberto }}
        accessibilityLabel={fixo ? titulo : `${titulo}, ${aberto ? t('Recolher') : t('Abrir')}`}
        style={({ pressed }) => [barra, estiloBarra, { opacity: pressed ? opac : 1 }]}>
        {seta === 'esquerda' && !fixo && setinha}
        <Text style={[estTitulo, estiloTitulo]} numberOfLines={nLinhas || undefined}>{visual === 'titulo' ? titulo.toUpperCase() : titulo}</Text>
        {!!resumo && <Text style={{ color: C.sub, fontSize: 12 }}>{resumo}</Text>}
        {direita}
        {seta === 'direita' && !fixo && setinha}
      </Pressable>
      {aberto && children}
    </View>
  );
}

/** Texto 26 (C1, REGRAS 5): cartão de apoio (painel, resumo) recolhível pelo título, com um resumo curto quando fechado.
 *  Começa recolhido (5.1) e o app lembra o que a pessoa deixou aberto ou fechado (5.4, pela chave). */
export function CartaoApoio({ titulo, resumo, chave, children, gap = 6, inicial = false, cor }: {
  titulo: string; resumo?: string; chave: string; children: ReactNode; gap?: number; inicial?: boolean; cor?: string }) {
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
      <Recolhivel titulo={titulo} resumo={resumo} chave={chave} gap={gap} inicial={inicial} cor={cor} estiloBarra={{ paddingVertical: 2 }}>
        {children}
      </Recolhivel>
    </View>
  );
}
